import type { BootOverrides, BootResult } from "./bootstrap";
import type {
	WeloopApi,
	WeloopEvent,
	WeloopEventHandler,
	WeloopStub,
} from "./types";

import { toCallable } from "./api";
import { bootstrap } from "./bootstrap";
import { rebuildConfigUrl } from "./project-id";
import { drainQueue } from "./queue";

// Mutable state owned by this module: the live mount, its configUrl (used to
// derive the sibling URL on `switchProject`), and the host page's own state
// that has to outlive any one mount. This is the orchestration layer that
// bootstrap.ts deliberately stays out of.
let currentHandle: BootResult | undefined = undefined;
// The last project asked for while a switch was running, applied when it
// ends. Like an elevator: presses during a trip only change the next stop,
// so A → B → C ends on C without mounting every project in between.
let nextProjectId: string | undefined = undefined;
// Calls the host made while a switch runs. Defined only during a switch:
// they are replayed on whichever mount is live when it ends, so an
// `identify()` is not lost with the mount being replaced.
let switchQueue: ((api: WeloopApi) => void)[] | undefined = undefined;
// Host `on()` subscriptions, re-attached to every new mount. Each mount keeps
// its own listener map, which dies with it.
let hostListeners: (readonly [WeloopEvent, WeloopEventHandler])[] = [];
// The host's last `hideLauncher()` / `showLauncher()`, so a new mount does
// not fall back to the `data-launcher` attribute. Undefined until either is
// called.
let launcher: boolean | undefined = undefined;
// The host's last `identify()`. A JWT session lives in the iframe that
// exchanged it and is not persisted, so each new mount needs it again.
let identity: { readonly token: string } | undefined = undefined;

type Forwarded = Exclude<keyof WeloopApi, "off" | "on" | "switchProject">;

// Every forwarded method takes at most one argument, already validated by
// `dispatch` or typed by the caller.
const later = (method: Forwarded, payload?: unknown): void => {
	const call = (api: WeloopApi): void => {
		// oxlint-disable-next-line typescript-eslint/no-unsafe-type-assertion -- the payload is the one this same method received from the host
		api[method](payload as never);
	};
	if (switchQueue !== undefined) {
		switchQueue.push(call);
	} else if (currentHandle !== undefined) {
		call(currentHandle.api);
	}
};

const boot = async (
	overrides?: BootOverrides,
): Promise<BootResult | undefined> => {
	try {
		return await bootstrap(overrides);
	} catch (error) {
		console.error("[weloop] bootstrap failed:", error);
		return undefined;
	}
};

const switchProject = (projectId: string): void => {
	if (switchQueue !== undefined) {
		nextProjectId = projectId;
		return;
	}
	const handle = currentHandle;
	if (handle === undefined) {
		return;
	}
	switchQueue = [];
	const identityBefore = identity;
	void (async () => {
		const next = await boot({
			configUrl: rebuildConfigUrl(handle.configUrl, projectId),
			launcher,
			projectId,
		});
		if (next === undefined) {
			// A missing config or a network error must not take down the
			// widget the page already has.
			console.error(
				`[weloop] switchProject to ${projectId} failed, still on the old project`,
			);
		} else {
			// An `open()` the old iframe never received – an email deep link
			// is consumed once per page, so it has nowhere else to go.
			const unsent = handle.dispose();
			currentHandle = next;
			for (const [event, handler] of hostListeners) {
				next.api.on(event, handler);
			}
			if (unsent !== undefined) {
				next.api.open(unsent);
			}
		}
		const live = next ?? handle;
		// A new mount needs the sign-in again; an `identify()` made during a
		// switch that failed still has to reach the old one.
		if (
			identity !== undefined &&
			(next !== undefined || identity !== identityBefore)
		) {
			live.api.identify(identity);
		}
		// A replayed call may queue another one (a host `open` handler that
		// calls `close`): the loop picks it up before the queue closes.
		for (const call of switchQueue ?? []) {
			call(live.api);
		}
		switchQueue = undefined;
		const queued = nextProjectId;
		nextProjectId = undefined;
		if (queued !== undefined && queued !== projectId) {
			switchProject(queued);
		}
	})();
};

// The api the host page holds for the whole page lifetime. It forwards to
// the live mount, so a project switch never leaves the page holding a
// disposed one. Before the first boot succeeds, every call is a no-op.
const facade: WeloopApi = {
	close: () => {
		later("close");
	},
	hideLauncher: () => {
		launcher = false;
		later("hideLauncher");
	},
	identify: (payload) => {
		identity = payload;
		// During a switch the call waits for the switch to end, which sends
		// the latest identity to whichever mount is then live.
		if (switchQueue === undefined) {
			later("identify", payload);
		}
	},
	off: (event, handler) => {
		hostListeners = hostListeners.filter(
			([known, callback]) => known !== event || callback !== handler,
		);
		currentHandle?.api.off(event, handler);
	},
	on: (event, handler) => {
		hostListeners.push([event, handler]);
		currentHandle?.api.on(event, handler);
	},
	open: (options) => {
		later("open", options);
	},
	showLauncher: () => {
		launcher = true;
		later("showLauncher");
	},
	switchProject: ({ projectId }) => {
		switchProject(projectId);
	},
	triggerSurvey: (options) => {
		later("triggerSurvey", options);
	},
};

const main = async (): Promise<void> => {
	// oxlint-disable-next-line typescript-eslint/no-unsafe-type-assertion -- window.weloop is the shared slot for stub-or-runtime; the stub fields are optional
	const stub = (window.weloop ?? {}) as WeloopStub;
	currentHandle = await boot();
	// After a failed boot `bootstrap()` already logged why; the facade then
	// answers with fast no-ops instead of the stub's silent buffering.
	window.weloop = toCallable(facade);
	drainQueue(stub, facade);
};

// Idempotency: a host page that includes the snippet twice (eg. a tag-manager
// tag AND a hand-written tag) will load this bundle twice. Bail on the second
// run so we don't mount two FABs / two iframes / two listener sets.
const installed = (window as Window & { __weloopBooted?: boolean })
	.__weloopBooted;
if (installed !== true) {
	(window as Window & { __weloopBooted?: boolean }).__weloopBooted = true;
	void main();
}
